import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './AdminShared.module.scss';
import {
    AdminVideoListItemDto,
    ParticipantDto,
    VideoParticipantCreateDto,
    VideoParticipantDto,
    VideoParticipantTypeDto,
    VideoParticipantUpdateDto
} from '../../../types/admin.types';
import {
    addVideoParticipant,
    fetchAdminVideosPaged,
    fetchParticipants,
    fetchVideoParticipantTypes,
    fetchVideoParticipants,
    removeVideoParticipant,
    updateVideoParticipant
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import SearchDropdown from './components/SearchDropdown';

const emptyForm = {
    ParticipantId: '',
    TypeId: ''
};

const SEARCH_DEBOUNCE_MS = 300;

const AdminVideoParticipants = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [videos, setVideos] = useState<AdminVideoListItemDto[]>([]);
    const [participants, setParticipants] = useState<ParticipantDto[]>([]);
    const [types, setTypes] = useState<VideoParticipantTypeDto[]>([]);
    const [videoParticipants, setVideoParticipants] = useState<VideoParticipantDto[]>([]);
    const [selectedVideoId, setSelectedVideoId] = useState<string>('');
    const [videoSearchInput, setVideoSearchInput] = useState('');
    const [participantSearchInput, setParticipantSearchInput] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [form, setForm] = useState(emptyForm);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingVideo, setIsLoadingVideo] = useState(false);
    const [isLoadingVideoOptions, setIsLoadingVideoOptions] = useState(false);
    const [isLoadingParticipantOptions, setIsLoadingParticipantOptions] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const selectedVideo = useMemo(
        () => videos.find((video) => video.Id === selectedVideoId) ?? null,
        [videos, selectedVideoId]
    );

    const videoOptions = useMemo(
        () => videos.map((video) => ({
            value: video.Id,
            label: video.Title,
            subLabel: video.Url
        })),
        [videos]
    );

    const participantOptions = useMemo(
        () => participants.map((participant) => ({
            value: participant.Id,
            label: participant.Nickname ? `${participant.Name} (${participant.Nickname})` : participant.Name
        })),
        [participants]
    );

    const roleOptions = useMemo(
        () => types.map((type) => ({
            value: type.Id,
            label: type.Name
        })),
        [types]
    );

    const loadVideoOptions = useCallback(async (query: string) => {
        setIsLoadingVideoOptions(true);
        try {
            const response = await fetchAdminVideosPaged(1, 50, query, [], token);
            setVideos((previous) => {
                const next = response.Results;
                if (!selectedVideoId || next.some((video) => video.Id === selectedVideoId)) {
                    return next;
                }

                const existingSelected = previous.find((video) => video.Id === selectedVideoId);
                return existingSelected ? [existingSelected, ...next] : next;
            });
        } catch (error) {
            setErrorMessage('Unable to load videos.');
        } finally {
            setIsLoadingVideoOptions(false);
        }
    }, [token, selectedVideoId]);

    const loadParticipantOptions = useCallback(async (query: string) => {
        setIsLoadingParticipantOptions(true);
        try {
            const response = await fetchParticipants(token, query);
            setParticipants((previous) => {
                const next = response;
                if (!form.ParticipantId || next.some((participant) => participant.Id === form.ParticipantId)) {
                    return next;
                }

                const existingSelected = previous.find((participant) => participant.Id === form.ParticipantId);
                return existingSelected ? [existingSelected, ...next] : next;
            });
        } catch (error) {
            setErrorMessage('Unable to load participants.');
        } finally {
            setIsLoadingParticipantOptions(false);
        }
    }, [token, form.ParticipantId]);

    const loadBaseData = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const [videosResponse, typesResponse] = await Promise.all([
                fetchAdminVideosPaged(1, 50, '', [], token),
                fetchVideoParticipantTypes(token)
            ]);
            const orderedVideos = [...videosResponse.Results];
            const orderedTypes = [...typesResponse].sort((a, b) => a.Name.localeCompare(b.Name));
            setVideos(orderedVideos);
            setTypes(orderedTypes);
            if (orderedVideos.length > 0) {
                setSelectedVideoId((current) => current || orderedVideos[0].Id);
            }
        } catch (error) {
            setErrorMessage('Unable to load video participant data.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const loadVideoParticipants = useCallback(async (videoId: string) => {
        setIsLoadingVideo(true);
        setErrorMessage(null);
        try {
            const response = await fetchVideoParticipants(videoId, token);
            setVideoParticipants(response);
        } catch (error) {
            setErrorMessage('Unable to load video participants.');
        } finally {
            setIsLoadingVideo(false);
        }
    }, [token]);

    useEffect(() => {
        loadBaseData();
    }, [loadBaseData]);

    useEffect(() => {
        if (!selectedVideoId) {
            setVideoParticipants([]);
            setIsLoadingVideo(false);
            return;
        }
        loadVideoParticipants(selectedVideoId);
    }, [selectedVideoId, loadVideoParticipants]);

    useEffect(() => {
        const handler = setTimeout(() => {
            loadVideoOptions(videoSearchInput);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(handler);
    }, [videoSearchInput, loadVideoOptions]);

    useEffect(() => {
        if (!participantSearchInput.trim()) {
            setParticipants((previous) => {
                if (!form.ParticipantId) {
                    return [];
                }

                return previous.filter((participant) => participant.Id === form.ParticipantId);
            });
            return;
        }

        const handler = setTimeout(() => {
            loadParticipantOptions(participantSearchInput);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(handler);
    }, [participantSearchInput, form.ParticipantId, loadParticipantOptions]);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
        setParticipantSearchInput('');
    };

    const handleEdit = (item: VideoParticipantDto) => {
        setSelectedId(item.Id);
        setForm({
            ParticipantId: item.ParticipantId,
            TypeId: item.TypeId
        });
        setParticipants((previous) => {
            if (previous.some((participant) => participant.Id === item.ParticipantId)) {
                return previous;
            }

            return [
                ...previous,
                {
                    Id: item.ParticipantId,
                    Name: item.ParticipantName,
                    Nickname: null,
                    DateOfBirth: null,
                    Attributes: []
                }
            ];
        });
    };

    const handleDelete = async (item: VideoParticipantDto) => {
        if (!selectedVideoId) {
            return;
        }
        setErrorMessage(null);
        try {
            await removeVideoParticipant(selectedVideoId, item.ParticipantId, token);
            if (selectedId === item.Id) {
                resetForm();
            }
            await loadVideoParticipants(selectedVideoId);
        } catch (error) {
            setErrorMessage('Unable to remove participant.');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedVideoId) {
            setErrorMessage('Select a video first.');
            return;
        }
        if (!form.ParticipantId || !form.TypeId) {
            setErrorMessage('Select both a participant and a role.');
            return;
        }

        setErrorMessage(null);
        try {
            if (selectedId) {
                const payload: VideoParticipantUpdateDto = {
                    Id: selectedId,
                    ParticipantId: form.ParticipantId,
                    TypeId: form.TypeId
                };
                await updateVideoParticipant(selectedVideoId, payload, token);
            } else {
                const payload: VideoParticipantCreateDto = {
                    VideoId: selectedVideoId,
                    ParticipantId: form.ParticipantId,
                    TypeId: form.TypeId
                };
                await addVideoParticipant(selectedVideoId, payload, token);
            }
            await loadVideoParticipants(selectedVideoId);
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save video participant.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Video Participants</h1>
                    <p>Assign participants to videos with their roles.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Assignments</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading videos...</div>
                    ) : videos.length === 0 ? (
                        <div className={styles.empty}>No videos available.</div>
                    ) : (
                        <div>
                            <div className={styles.field}>
                                <label htmlFor="video-select">Video</label>
                                <SearchDropdown
                                    id="video-select"
                                    value={selectedVideoId}
                                    options={videoOptions}
                                    onChange={(value) => {
                                        setSelectedVideoId(value ?? '');
                                        resetForm();
                                    }}
                                    onSearchChange={setVideoSearchInput}
                                    placeholder="Search videos"
                                    noResultsText="No videos found."
                                    isLoading={isLoadingVideoOptions}
                                    clearable={false}
                                />
                            </div>
                            <div className={styles.muted}>
                                {selectedVideo ? `Selected: ${selectedVideo.Title}` : 'Select a video to view participants.'}
                            </div>
                            {isLoadingVideo ? (
                                <div className={styles.muted}>Loading participants...</div>
                            ) : videoParticipants.length === 0 ? (
                                <div className={styles.empty}>No participants assigned to this video.</div>
                            ) : (
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Participant</th>
                                            <th>Role</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {videoParticipants.map((item) => (
                                            <tr key={item.Id}>
                                                <td>{item.ParticipantName}</td>
                                                <td>{item.TypeName}</td>
                                                <td>
                                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>
                                                        Edit
                                                    </button>{' '}
                                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item)}>
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Assignment' : 'New Assignment'}</h2>
                    {selectedVideoId ? (
                        <form onSubmit={handleSubmit}>
                            <div className={styles.field}>
                                <label htmlFor="participant-select">Participant</label>
                                <SearchDropdown
                                    id="participant-select"
                                    value={form.ParticipantId}
                                    options={participantOptions}
                                    onChange={(value) => setForm({ ...form, ParticipantId: value ?? '' })}
                                    onSearchChange={setParticipantSearchInput}
                                    placeholder="Search participants"
                                    noResultsText="Type to search participants."
                                    isLoading={isLoadingParticipantOptions}
                                />
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="participant-role">Role</label>
                                <SearchDropdown
                                    id="participant-role"
                                    value={form.TypeId}
                                    options={roleOptions}
                                    onChange={(value) => setForm({ ...form, TypeId: value ?? '' })}
                                    placeholder="Search roles"
                                    noResultsText="No roles found."
                                    clearable={false}
                                />
                            </div>
                            <div className={styles.actions}>
                                <button className={styles.primary} type="submit">
                                    {selectedId ? 'Update Assignment' : 'Add Assignment'}
                                </button>
                                {selectedId ? (
                                    <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                                ) : null}
                            </div>
                        </form>
                    ) : (
                        <div className={styles.muted}>Select a video to manage assignments.</div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default AdminVideoParticipants;
